import { makeAutoObservable, observable, runInAction } from "mobx";
import Vector from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import ImageLayer from "ol/layer/Image";
import ImageStatic from "ol/source/ImageStatic";
import OpenLayersMap from "ol/Map";
import View from "ol/View";
import { Geometry } from "ol/geom";
import { Feature } from "ol";
import { Fill, Stroke, Style } from "ol/style";
import GeoJSON from "ol/format/GeoJSON";
import { isEmpty as isEmptyExtent } from "ol/extent";
import * as fieldsService from "@services/fieldsService";
import {
  TLegendItem,
  TStatsItem,
  TYieldScale,
  TCameraStats,
  TNdviDateItem,
  Season,
  TProductivityLegendItem,
  TProductivityStats,
  TFieldTrackDay,
  TFieldTrackDevice,
  TEveningStrength,
  THeatmapResponse,
} from "@services/fieldsService";
import updateFeatureStyle, { colorByLegend } from "@screens/FieldsScreen/fieldsScreenUtils";
import {
  buildFieldTrackFeatures,
  dateSequenceColor,
  installTrackClip,
  resolveTrackMetric,
  TTrackColorMode,
  TTrackGroup,
  TTrackMetric,
} from "@screens/FieldsScreen/fieldTracksLayer";
import { deviceColorAt } from "@screens/TracksScreen/utils";
import { LinkedCursorLayer } from "@screens/FieldsScreen/linkedCursorLayer";
import SeasonsStore from "@stores/SeasonStore";
import {
  listCustomLayers,
  getCustomLayer,
  type TCustomLayer,
} from "@src/services/customLayersService";
import {
  CAMERA_METRIC_BY_VIEW,
  GEOJSON_OPTS,
  customLayerIdOf,
  isCameraView,
  isCustomView,
  isSpectralView,
  type TFieldMapView,
} from "@screens/FieldsScreen/fieldLayers";
import { createBaseImageryLayers } from "@screens/FieldsScreen/baseLayers";
import {
  assembleTrackGroups,
  devicesForLocalDay,
  localDayIndex,
  splitBatchByDay,
} from "@screens/FieldsScreen/fieldTracksCache";
import { TrackTimeFocus } from "@screens/FieldsScreen/fieldTrackTime";

/** Compare-view field layers — the same union as the main map (fieldLayers.ts). */
type CompareViewMode = TFieldMapView;

const optOptions = GEOJSON_OPTS;

const fieldStyle = [
  new Style({
    stroke: new Stroke({ color: "#F6FF8F", width: 3 }),
    fill: new Fill({ color: "rgba(246,255,143,0.5)" }),
  }),
];


export class CompareVM {
  // ── Layer selector ────────────────────────────────────────
  viewMode: CompareViewMode = "border";

  // NDVI / brightness / moisture share one date list + overlay (see FieldsScreenVM).
  isSpectralView = isSpectralView;

  get spectralLayer(): "ndvi" | "brightness" | "moisture" {
    return this.isSpectralView(this.viewMode) ? this.viewMode : "ndvi";
  }

  // Potato-camera layers reuse the yield heatmap layer/source (same GeoJSON
  // hexagon contract), so only the fetch + legend/stats differ.
  isCameraView = isCameraView;

  cameraLegend: TLegendItem[] = [];
  cameraStats: TCameraStats = null;

  // Custom layers (customer-supplied maps) reuse the yield heatmap layer the
  // same way the camera layers do; the view mode carries the layer id because
  // a field can hold several. See FieldsScreenVM for the full note.
  isCustomView = isCustomView;
  customLayerIdOf = customLayerIdOf;

  customLayers: TCustomLayer[] = [];
  customLegend: TLegendItem[] = [];

  get activeCustomLayer(): TCustomLayer | null {
    const layerId = this.customLayerIdOf(this.viewMode);
    if (!layerId) return null;
    return this.customLayers.find((l) => l.id === layerId) ?? null;
  }

  loadCustomLayers = async () => {
    const layers = await listCustomLayers(this.fieldId);
    runInAction(() => {
      this.customLayers = layers;
    });
  };

  private loadCustomLayer = async (layerId: string) => {
    const req = ++this.heatmapReq;
    this.loading = true;
    try {
      const res = await getCustomLayer(this.fieldId, layerId);
      if (req !== this.heatmapReq) return;
      const format = this.heatmapSource.getFormat() ?? new GeoJSON();
      const features = format.readFeatures(res.features, optOptions) as Feature<Geometry>[];
      features.forEach((f) => {
        f.setId(f.get("id"));
        updateFeatureStyle(f);
      });
      this.heatmapSource.clear(true);
      this.heatmapSource.addFeatures(features);
      this.customLegend = res.legend;
      this.resetLayers();
    } catch (e) {
      console.warn("CompareVM: failed to load custom layer", e);
    } finally {
      if (req === this.heatmapReq) this.loading = false;
    }
  };

  // ── Season (independent from global) ──────────────────────
  seasonId: string;

  // ── Yield state ───────────────────────────────────────────
  yieldScale: TYieldScale = "quantize";
  heatmapLegend: TLegendItem[] = [];
  heatmapStatistics: TStatsItem | null = null;
  private heatmapSource = new Vector({ format: new GeoJSON() });
  private heatmapLayer = new VectorLayer({ source: this.heatmapSource, style: fieldStyle });

  // ── Evening result ("Even out yield" side by side) ────────
  /**
   * Set while the yield-evening panel uses this pane to show its result next
   * to the original on the main map. The pane then shows a label instead of
   * its own layer controls, and its map is whatever the panel hands it.
   */
  eveningStrength: TEveningStrength | null = null;

  /**
   * Show the evening panel's result, colored on `scale` — the main pane's
   * legend — so a color means the same yield on both sides.
   */
  showEveningResult = (
    response: THeatmapResponse,
    scale: TLegendItem[],
    strength: TEveningStrength
  ) => {
    this.eveningStrength = strength;
    // Clear before building the replacement so the old and new hexes are never
    // held at once.
    this.heatmapSource.clear(true);
    const format = this.heatmapSource.getFormat() ?? new GeoJSON();
    const features = format.readFeatures(response.features, optOptions) as Feature<Geometry>[];
    features.forEach((f) => f.setId(f.get("id")));
    this.heatmapLegend = colorByLegend(features, scale.length > 0 ? scale : response.legend);
    this.heatmapStatistics = response.stats;
    this.heatmapSource.addFeatures(features);
    this.resetLayers();
  };

  setLoading = (v: boolean) => (this.loading = v);

  // ── Tracks state ──────────────────────────────────────────
  // Its own copy of everything: the whole point of compare is that the two
  // panes show different things, so sharing the main VM's day/metric selection
  // would collapse yield-left/tracks-right into one view.
  trackDays: TFieldTrackDay[] = [];
  trackDevices: TFieldTrackDevice[] = [];
  trackMetric: TTrackMetric = "sensor";
  trackColorMode: TTrackColorMode = "value";
  /** Machines currently drawn on this pane. Seeded from the day summary and
   *  then owned by the user, exactly as on the main panel. */
  selectedTrackDeviceIds: string[] = [];
  /** Set once the user hides or shows a machine here; from then on a reload of
   *  this pane must not overwrite their choice. */
  private trackSelectionTouched = false;
  tracksLoading = false;
  /** Per-day points, mirroring FieldsScreenVM. Not observable: it holds tens of
   *  thousands of rows and nothing renders from it directly. */
  trackDayCache = new Map<string, TFieldTrackDevice[]>();
  private tracksSource = new Vector({ format: new GeoJSON() });
  private tracksLayer = new VectorLayer({ source: this.tracksSource, style: fieldStyle });
  /** Days per request, matching FieldsScreenVM. */
  private static readonly DAYS_PER_BATCH = 3;
  /** This pane's own focused day and start/stop window — yield on the left,
   *  one day by time of pass on the right. See FieldsScreenVM.focusTrackDay. */
  trackTime = new TrackTimeFocus((day) => {
    void this.trackDevices;
    return devicesForLocalDay(day, this.trackSourceDays(day), this.trackDayCache);
  });
  private trackColorModeBeforeFocus: TTrackColorMode | null = null;

  // ── NDVI state ────────────────────────────────────────────
  ndviDates: TNdviDateItem[] = [];
  ndviTotal = 0;
  ndviSelectedDate: string | null = null;
  ndviImageUrl: string | null = null;
  ndviLoading = false;
  private ndviLayer = new ImageLayer({ opacity: 0.85 });
  private ndviThumbnailCache = new Map<string, string>();

  // ── Productivity state (read-only: shows the stored map only) ─
  productivityReady = false;
  productivityLegend: TProductivityLegendItem[] = [];
  productivityStats: TProductivityStats | null = null;
  productivityLoading = false;
  private productivitySource = new Vector({ format: new GeoJSON() });
  private productivityLayer = new VectorLayer({ source: this.productivitySource, style: fieldStyle });

  // ── Map ───────────────────────────────────────────────────
  loading = false;

  private fieldsLayer: VectorLayer<Vector<Geometry>> = null!;
  private satelliteLayer: any = null;
  private mapboxFallbackLayer: any = null;
  private maptileLayer: any = null;

  map: OpenLayersMap = null!;

  /** Ghost of the MAIN map's pointer / pinned cell, drawn on this pane. */
  linkedCursor = new LinkedCursorLayer();

  /** True for the data layers whose shapes are worth mirroring onto the other
   *  pane (cells, zones, swaths). Base imagery and the field outline are not:
   *  both panes already draw the same field. */
  // Custom layers are excluded here for the same reason as in FieldsScreenVM:
  // their dissolved zone polygons outline a third of the field, not one cell.
  isMirrorLayer = (layer: unknown): boolean => {
    if (this.isCustomView(this.viewMode)) return false;
    return (
      layer === this.heatmapLayer ||
      layer === this.productivityLayer ||
      layer === this.tracksLayer
    );
  };

  constructor(
    private sharedFieldsSource: Vector<Geometry>,
    private seasonStore: SeasonsStore,
    primaryView: View,
    public fieldId: string,
    /** Open as the yield-evening panel's result pane (see eveningStrength). */
    evening?: TEveningStrength
  ) {
    if (evening) {
      this.eveningStrength = evening;
      this.viewMode = "yield";
    }

    // Own layers (each map needs its own layer instances)
    this.fieldsLayer = new VectorLayer({ source: sharedFieldsSource, style: fieldStyle });
    [this.satelliteLayer, this.mapboxFallbackLayer, this.maptileLayer] = createBaseImageryLayers();

    this.seasonId = seasonStore.currentSeasonId ?? "";

    // Create map with synced initial view
    this.map = new OpenLayersMap({
      layers: [
        this.satelliteLayer,
        this.mapboxFallbackLayer,
        this.maptileLayer,
        this.fieldsLayer,
        this.linkedCursor.layer,
      ],
      controls: [],
      view: new View({
        center: primaryView.getCenter(),
        resolution: primaryView.getResolution(),
        maxZoom: 22,
      }),
    });

    // Same boundary mask as the main pane. Without it this map drew the
    // machine's road trips to and from the field across the whole view.
    installTrackClip(this.tracksLayer, this.map, () =>
      this.sharedFieldsSource.getFeatureById(this.fieldId)?.getGeometry()
    );

    makeAutoObservable(this, {
      map: false,
      // Tens of thousands of rows nothing renders from directly — deep
      // observation would be pure overhead.
      trackDayCache: false,
      // Same rows as trackDayCache; shallow so they are not cloned into MobX
      // proxies on every batch. See FieldsScreenVM's constructor.
      trackDevices: observable.ref,
      // Observable on its own; the reference never changes.
      trackTime: false,
      // Driven imperatively from pointer events at up to 60 fps; nothing
      // renders from it through React.
      linkedCursor: false,
      heatmapReq: false,
      productivityReq: false,
      trackReq: false,
    });

    // The right pane needs its own copy of the field's custom layers so its
    // dropdown can offer them independently of the left pane. The evening
    // pane has no dropdown.
    if (!evening) void this.loadCustomLayers();
  }

  // ── Request tokens ────────────────────────────────────────
  // Latest request per kind; a response from an older one is dropped instead
  // of painting the previous field/season/view into this pane. Not observable.
  heatmapReq = 0;
  productivityReq = 0;
  trackReq = 0;

  /** Any field/season/scale/view change: whatever is in flight is stale. */
  private cancelInFlight = () => {
    this.heatmapReq++;
    this.productivityReq++;
    this.loading = false;
    this.productivityLoading = false;
  };

  // ── Actions ───────────────────────────────────────────────

  setViewMode = (v: CompareViewMode) => {
    this.cancelInFlight();
    this.viewMode = v;
    this.resetLayers();

    if (v === "yield") {
      this.loadHeatmap();
    } else if (this.isCameraView(v)) {
      this.loadCameraLayer();
    } else if (this.isSpectralView(v)) {
      if (this.ndviDates.length > 0) {
        // Switching among spectral layers on the same field → keep the dates,
        // just re-render the overlay for the newly-selected layer.
        if (this.ndviSelectedDate) this.loadNdviImage(this.ndviSelectedDate);
      } else {
        this.clearNdviState();
        this.ndviLoading = true;
        this.loadNdviDates().finally(() => { this.ndviLoading = false; });
      }
    } else if (v === "productivity") {
      this.loadProductivity();
    } else if (v === "tracks") {
      this.loadTracks();
    } else if (this.isCustomView(v)) {
      const layerId = this.customLayerIdOf(v);
      if (layerId) this.loadCustomLayer(layerId);
    }
    if (v !== "tracks") this.clearTrackState();
  };

  setSeasonId = (id: string) => {
    this.cancelInFlight();
    this.seasonId = id;
    if (this.viewMode === "yield") {
      this.loadHeatmap();
    } else if (this.isCameraView(this.viewMode)) {
      this.loadCameraLayer();
    } else if (this.viewMode === "tracks") {
      // Days belong to a season; keeping the old selection would offer days
      // that don't exist in the newly chosen one.
      this.loadTracks();
    }
  };

  setYieldScale = (v: TYieldScale) => {
    this.cancelInFlight();
    this.yieldScale = v;
    if (this.viewMode === "yield") {
      this.loadHeatmap();
    } else if (this.isCameraView(this.viewMode)) {
      this.loadCameraLayer();
    }
  };

  setNdviSelectedDate = (date: string) => {
    this.ndviSelectedDate = date;
    this.loadNdviImage(date);
  };

  loadMoreNdviDates = () => {
    if (this.ndviDates.length >= this.ndviTotal) return;
    this.loadNdviDates(this.ndviDates.length);
  };

  getNdviThumbnail = async (date: string): Promise<string> => {
    const layer = this.spectralLayer;
    const key = `${layer}:${this.fieldId}:${date}`;
    const cached = this.ndviThumbnailCache.get(key);
    if (cached) return cached;
    const url = await fieldsService.getSpectralImageUrl(this.fieldId, date, layer, true);
    this.ndviThumbnailCache.set(key, url);
    return url;
  };

  get seasons(): Season[] {
    return this.seasonStore.seasons;
  }

  /** Called when the user selects a different field while compare mode is active */
  updateFieldId = (newFieldId: string) => {
    this.cancelInFlight();
    this.fieldId = newFieldId;
    // Custom layers belong to a field, so a `custom:<id>` view mode does not
    // survive a field change — refetch the list and drop back to boundaries if
    // the layer on screen isn't one of the new field's.
    this.customLayers = [];
    void this.loadCustomLayers();
    if (this.isCustomView(this.viewMode)) {
      this.setViewMode("border");
      return;
    }
    // Reload data for the new field based on current view mode
    if (this.viewMode === "yield") {
      this.loadHeatmap();
    } else if (this.isCameraView(this.viewMode)) {
      this.loadCameraLayer();
    } else if (this.isSpectralView(this.viewMode)) {
      this.clearNdviState();
      this.ndviLoading = true;
      this.loadNdviDates().finally(() => { this.ndviLoading = false; });
    } else if (this.viewMode === "productivity") {
      this.loadProductivity();
    } else if (this.viewMode === "tracks") {
      this.loadTracks();
    }
  };

  // ── Private ───────────────────────────────────────────────

  private resetLayers = () => {
    // Keep the mapbox fallback: it's the only source above zoom 18, so dropping
    // it here left the right pane with no imagery when zoomed in.
    const layers: any[] = [
      this.satelliteLayer,
      this.mapboxFallbackLayer,
      this.maptileLayer,
      this.fieldsLayer,
    ];
    if (
      this.viewMode === "yield" ||
      this.isCameraView(this.viewMode) ||
      this.isCustomView(this.viewMode)
    )
      layers.push(this.heatmapLayer);
    if (this.isSpectralView(this.viewMode) && this.ndviLayer.getSource()) layers.push(this.ndviLayer);
    if (this.viewMode === "productivity" && this.productivitySource.getFeatures().length > 0) {
      layers.push(this.productivityLayer);
    }
    if (this.viewMode === "tracks") layers.push(this.tracksLayer);
    // Always on top: the mirrored pointer has to stay visible over every layer.
    layers.push(this.linkedCursor.layer);
    this.map.setLayers(layers);
  };

  // ── Tracks ────────────────────────────────────────────────

  /** Local day → the UTC days it is fetched under. See FieldsScreenVM. */
  get trackLocalDayIndex(): Map<string, string[]> {
    return localDayIndex(this.trackDays);
  }

  /** The worked days in the viewer's local calendar, as listed. */
  get trackDayList(): string[] {
    return [...this.trackLocalDayIndex.keys()];
  }

  /** The UTC days the backend buckets by — what is fetched and cached. */
  get trackFetchDayList(): string[] {
    return [...new Set(this.trackDays.map((d) => d.day))].sort();
  }

  trackSourceDays = (localDay: string): string[] =>
    this.trackLocalDayIndex.get(localDay) ?? [];

  /** Stable machine order for this pane, by first appearance in the day list.
   *  Independent of the left pane: the two panes can show different seasons. */
  get trackDeviceList(): Array<{ deviceId: string; name: string; color: string }> {
    const names = new Map<string, string>();
    for (const d of this.trackDays) {
      if (!names.has(d.deviceId)) names.set(d.deviceId, d.name || d.deviceId);
    }
    for (const d of this.trackDevices) names.set(d.deviceId, d.name || d.deviceId);
    return [...names.entries()].map(([deviceId, name], i) => ({
      deviceId,
      name,
      color: deviceColorAt(i),
    }));
  }

  trackDeviceColor = (deviceId: string): string =>
    this.trackDeviceList.find((d) => d.deviceId === deviceId)?.color ?? "#888888";

  setTrackMetric = (m: TTrackMetric) => {
    this.trackMetric = m;
    this.rebuildTrackFeatures();
  };

  /** Drop back to the sensor metric when the machines on this pane cannot serve
   *  the selected one. The two panes can show different seasons, so this pane
   *  reaches the decision on its own. See resolveTrackMetric. */
  private ensureMetricAvailable = () => {
    this.trackMetric = resolveTrackMetric(this.trackMetric, this.trackDevices);
  };

  setTrackColorMode = (m: TTrackColorMode) => {
    this.trackColorMode = m;
    this.rebuildTrackFeatures();
  };

  focusTrackDay = (day: string) => {
    const wasFocused = this.trackTime.day != null;
    this.trackTime.toggle(day);
    if (this.trackTime.day != null && !wasFocused) {
      this.trackColorModeBeforeFocus = this.trackColorMode;
      this.trackColorMode = "date";
    } else if (this.trackTime.day == null) {
      this.restoreColorModeAfterFocus();
    }
    this.rebuildTrackFeatures();
  };

  private restoreColorModeAfterFocus = () => {
    if (this.trackColorModeBeforeFocus) this.trackColorMode = this.trackColorModeBeforeFocus;
    this.trackColorModeBeforeFocus = null;
  };

  setTrackTimeWindow = (from: number, to: number) => {
    this.trackTime.setWindow(from, to);
    this.rebuildTrackFeatures();
  };

  /** Show/hide a machine on this pane. A display filter only — every machine's
   *  points are already loaded, so it never touches the network. */
  toggleTrackDevice = (deviceId: string) => {
    this.trackSelectionTouched = true;
    this.selectedTrackDeviceIds = this.selectedTrackDeviceIds.includes(deviceId)
      ? this.selectedTrackDeviceIds.filter((d) => d !== deviceId)
      : [...this.selectedTrackDeviceIds, deviceId];
    this.rebuildTrackFeatures();
  };

  private clearTrackState = () => {
    // Batches in flight belong to the state being cleared.
    this.trackReq++;
    this.trackDays = [];
    this.trackDevices = [];
    this.selectedTrackDeviceIds = [];
    this.trackSelectionTouched = false;
    this.trackDayCache.clear();
    this.trackTime.clear();
    this.restoreColorModeAfterFocus();
    this.tracksSource.clear(true);
  };

  private loadTracks = async () => {
    this.clearTrackState();
    const req = this.trackReq;
    this.tracksLoading = true;
    try {
      const summary = await fieldsService.getFieldTracks(this.fieldId, this.seasonId);
      // Field/season/view changed meanwhile — this summary is someone else's.
      if (req !== this.trackReq || summary.failed) return;
      this.trackDays = summary.days;
      // Everything visible until the user says otherwise.
      if (!this.trackSelectionTouched) {
        this.selectedTrackDeviceIds = [...new Set(summary.days.map((d) => d.deviceId))];
      }
      await this.loadTrackPoints();
    } catch (e) {
      console.warn(e);
    } finally {
      this.tracksLoading = false;
    }
  };

  /** Loads every worked day, in batches, caching each so nothing is fetched
   *  twice. Mirrors FieldsScreenVM — see there for why days are batched. */
  private loadTrackPoints = async () => {
    const days = [...this.trackFetchDayList];
    if (days.length === 0) {
      this.trackDevices = [];
      this.tracksSource.clear(true);
      return;
    }
    const missing = days.filter((d) => !this.trackDayCache.has(d));
    if (missing.length === 0) {
      this.rebuildTrackFeatures();
      return;
    }

    const req = this.trackReq;
    this.tracksLoading = true;
    // Redraw the cached days once; each batch then appends only its own.
    this.rebuildTrackFeatures();
    const batches: string[][] = [];
    const ordered = [...missing].sort().reverse();
    for (let i = 0; i < ordered.length; i += CompareVM.DAYS_PER_BATCH) {
      batches.push(ordered.slice(i, i + CompareVM.DAYS_PER_BATCH));
    }
    try {
      for (const batch of batches) {
        const data = await fieldsService.getFieldTracks(
          this.fieldId,
          this.seasonId,
          batch
        );
        // Dropped while in flight (field/season/view changed): never cache it.
        if (req !== this.trackReq) return;
        // A failed batch is not "no points": leave its days uncached.
        if (data.failed) break;
        for (const [day, slice] of splitBatchByDay(batch, data.devices)) {
          this.trackDayCache.set(day, slice);
        }
        this.trackDevices = [...this.trackDayCache.values()].flat();
        const metric = this.trackMetric;
        this.ensureMetricAvailable();
        // A focused day's time colours span every batch it needs.
        if (metric !== this.trackMetric || this.trackTime.day != null) this.rebuildTrackFeatures();
        else this.appendTrackFeatures(batch);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      this.tracksLoading = false;
    }
  };

  /** Days on the same red-to-green ramp as the main pane, so yield-left /
   *  tracks-by-date-right can be read without cross-referencing legends. */
  get trackDayLegend(): Array<{ day: string; color: string }> {
    const days = this.trackDayList;
    return days.map((day, i) => ({ day, color: dateSequenceColor(i, days.length) }));
  }

  trackDayColor = (day: string): string =>
    this.trackDayLegend.find((d) => d.day === day)?.color ?? "#888888";

  private assembleGroups = (onlyDays?: string[]): TTrackGroup[] => {
    const focus = this.trackTime.day;
    if (focus) {
      // One run per machine across the UTC days the local day overlaps, so
      // the swath has no seam at UTC midnight (a US evening). A focused day
      // is always rebuilt whole, never appended per batch.
      const visible = new Set(this.selectedTrackDeviceIds);
      return devicesForLocalDay(focus, this.trackSourceDays(focus), this.trackDayCache)
        .filter((device) => visible.has(device.deviceId))
        .map((device) => ({ day: focus, device }));
    }
    return assembleTrackGroups(this.trackFetchDayList, this.trackDayCache, this.selectedTrackDeviceIds, onlyDays);
  };

  private rebuildTrackFeatures = () => {
    // Clear first so old and new feature sets are never both alive.
    this.tracksSource.clear(true);
    this.appendTrackFeatures();
  };

  /** Draw the given days (default: all) on top of what is drawn — see
   *  FieldsScreenVM.appendTrackFeatures. */
  private appendTrackFeatures = (onlyDays?: string[]) => {
    const { features } = buildFieldTrackFeatures({
      groups: this.assembleGroups(onlyDays),
      metric: this.trackMetric,
      colorMode: this.trackColorMode,
      colorForDevice: this.trackDeviceColor,
      colorForDay: this.trackDayColor,
      time: this.trackTime.render,
    });
    this.tracksSource.addFeatures(features);
    this.resetLayers();
  };

  /** Read-only: load the stored productivity map (no generation/regenerate). */
  private loadProductivity = async () => {
    const req = ++this.productivityReq;
    this.productivityLoading = true;
    this.productivitySource.clear(true);
    this.productivityReady = false;
    this.productivityLegend = [];
    this.productivityStats = null;
    try {
      const res = await fieldsService.getProductivityMap(this.fieldId);
      if (req !== this.productivityReq) return;
      if (res && res.geojson && res.legend) {
        const format = this.productivitySource.getFormat() ?? new GeoJSON();
        const features = format.readFeatures(res.geojson, optOptions) as Feature<Geometry>[];
        // Zone colors live on each feature's fill/stroke props — without this the
        // features fall back to the layer's yellow fieldStyle (same as the main VM).
        features.forEach((f) => updateFeatureStyle(f));
        this.productivitySource.clear(true);
        this.productivitySource.addFeatures(features);
        this.productivityLegend = res.legend;
        this.productivityStats = res.stats ?? null;
        this.productivityReady = true;
        this.resetLayers();
      }
    } catch (e) {
      console.warn("CompareVM: failed to load productivity map", e);
    } finally {
      if (req === this.productivityReq) this.productivityLoading = false;
    }
  };

  private loadHeatmap = async () => {
    const req = ++this.heatmapReq;
    this.loading = true;
    try {
      const res = await fieldsService.getHeatmapData(this.fieldId, this.seasonId, this.yieldScale);
      if (req !== this.heatmapReq) return;
      if (res.failed) {
        // Nothing on screen beats the placeholder's "0 t" (already toasted).
        this.heatmapSource.clear(true);
        this.heatmapLegend = [];
        this.heatmapStatistics = null;
        return;
      }
      const format = this.heatmapSource.getFormat() ?? new GeoJSON();
      const features = format.readFeatures(res.features, optOptions) as Feature<Geometry>[];
      features.forEach((f) => {
        f.setId(f.get("id"));
        updateFeatureStyle(f);
      });
      this.heatmapSource.clear(true);
      this.heatmapSource.addFeatures(features);
      this.heatmapLegend = res.legend;
      this.heatmapStatistics = res.stats;
      this.resetLayers();
    } catch (e) {
      console.warn("CompareVM: failed to load heatmap", e);
    } finally {
      if (req === this.heatmapReq) this.loading = false;
    }
  };

  private loadCameraLayer = async () => {
    const metric = CAMERA_METRIC_BY_VIEW[this.viewMode];
    if (!metric) return;
    const req = ++this.heatmapReq;
    this.loading = true;
    try {
      const res = await fieldsService.getCameraHeatmapData(
        this.fieldId,
        this.seasonId,
        metric,
        this.yieldScale
      );
      if (req !== this.heatmapReq) return;
      const format = this.heatmapSource.getFormat() ?? new GeoJSON();
      const features = format.readFeatures(res.features, optOptions) as Feature<Geometry>[];
      features.forEach((f) => {
        f.setId(f.get("id"));
        updateFeatureStyle(f);
      });
      this.heatmapSource.clear(true);
      this.heatmapSource.addFeatures(features);
      this.cameraLegend = res.legend;
      this.cameraStats = res.stats;
      this.resetLayers();
    } catch (e) {
      console.warn("CompareVM: failed to load camera layer", e);
    } finally {
      if (req === this.heatmapReq) this.loading = false;
    }
  };

  private loadNdviDates = async (offset = 0) => {
    try {
      const res = await fieldsService.getNdviDates(
        this.fieldId,
        fieldsService.NDVI_DATE_PAGE_SIZE,
        offset
      );
      if (offset === 0) {
        this.ndviDates = res.dates;
      } else {
        this.ndviDates = [...this.ndviDates, ...res.dates];
      }
      this.ndviTotal = res.total;
      if (!this.ndviSelectedDate && res.dates.length > 0) {
        this.setNdviSelectedDate(res.dates[0].date);
      }
    } catch (e) {
      console.warn("CompareVM: failed to load NDVI dates", e);
    }
  };

  private loadNdviImage = async (date: string) => {
    this.ndviLoading = true;
    try {
      if (this.ndviImageUrl) {
        URL.revokeObjectURL(this.ndviImageUrl);
        this.ndviImageUrl = null;
      }
      const objectUrl = await fieldsService.getSpectralImageUrl(
        this.fieldId,
        date,
        this.spectralLayer,
        false
      );
      this.ndviImageUrl = objectUrl;

      const field = this.sharedFieldsSource.getFeatureById(this.fieldId);
      if (!field) return;
      const extent = field.getGeometry()?.getExtent();
      if (!extent || isEmptyExtent(extent)) return;

      this.ndviLayer.setSource(new ImageStatic({ url: objectUrl, imageExtent: extent }));
      this.resetLayers();
    } catch (e) {
      console.warn("CompareVM: failed to load NDVI image", e);
    } finally {
      this.ndviLoading = false;
    }
  };

  private clearNdviState = () => {
    this.ndviDates = [];
    this.ndviTotal = 0;
    this.ndviSelectedDate = null;
    if (this.ndviImageUrl) {
      URL.revokeObjectURL(this.ndviImageUrl);
      this.ndviImageUrl = null;
    }
    this.ndviLayer.setSource(null);
  };

  // ── Cleanup ───────────────────────────────────────────────

  dispose = () => {
    this.clearNdviState();
    // Revoke thumbnail URLs
    for (const url of this.ndviThumbnailCache.values()) {
      URL.revokeObjectURL(url);
    }
    this.ndviThumbnailCache.clear();
    this.heatmapSource.clear(true);
    this.productivitySource.clear(true);
    this.linkedCursor.hide();
    this.map.setTarget(undefined);
  };
}
